/**
 * 広告画像エディタ: グループを「1 つのまとまり（ユニット）」として扱うための共通処理。
 *
 * - 整列 / 等間隔: グループはメンバー全体の外接矩形で 1 つとして扱い、メンバーは同じ量だけ動く
 *   （グループ内の位置関係は崩さない）。グループ編集中（activeGroupId 一致）のメンバーは個別要素として扱う。
 * - 複製（コピー＆ペースト / Option+ドラッグ）: グループ丸ごとの複製は「別の新しいグループ」として作る。
 *   グループ編集中にメンバーの一部だけを複製した場合は、同じグループに入れる（従来どおり）。
 */
import type { AdElement, AdGroup, AdImageSlide } from '@goenhub/shared';

export interface SelectionUnit {
  /** 'g:<groupId>' または 'e:<elementId>' */
  key: string;
  /** 丸ごと扱うグループ ID（個別要素なら null） */
  groupId: string | null;
  memberIds: string[];
}

export interface UnitBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 選択 ID をユニットにまとめる（出現順を保持、重複なし）。 */
export function resolveUnits(
  elements: AdElement[],
  selectedIds: string[],
  activeGroupId: string | null,
): SelectionUnit[] {
  const units: SelectionUnit[] = [];
  const seen = new Set<string>();
  for (const id of selectedIds) {
    const el = elements.find((e) => e.id === id);
    if (!el) continue;
    const gid = el.groupId && el.groupId !== activeGroupId ? el.groupId : null;
    const key = gid ? `g:${gid}` : `e:${el.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    units.push({
      key,
      groupId: gid,
      memberIds: gid ? elements.filter((e) => e.groupId === gid).map((e) => e.id) : [el.id],
    });
  }
  return units;
}

/** ユニットの外接矩形。 */
export function unitBox(elements: AdElement[], unit: SelectionUnit): UnitBox {
  const members = elements.filter((e) => unit.memberIds.includes(e.id));
  const minX = Math.min(...members.map((e) => e.x));
  const minY = Math.min(...members.map((e) => e.y));
  const maxX = Math.max(...members.map((e) => e.x + e.w));
  const maxY = Math.max(...members.map((e) => e.y + e.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** ユニットごとの移動量を要素に適用する。 */
export function applyUnitDeltas(
  elements: AdElement[],
  units: SelectionUnit[],
  deltas: Map<string, { dx: number; dy: number }>,
): AdElement[] {
  const byElement = new Map<string, { dx: number; dy: number }>();
  for (const u of units) {
    const d = deltas.get(u.key);
    if (!d || (d.dx === 0 && d.dy === 0)) continue;
    for (const id of u.memberIds) byElement.set(id, d);
  }
  if (byElement.size === 0) return elements;
  return elements.map((el) => {
    const d = byElement.get(el.id);
    return d ? { ...el, x: Math.round(el.x + d.dx), y: Math.round(el.y + d.dy) } : el;
  });
}

export type AlignMode = 'left' | 'right' | 'top' | 'bottom' | 'hcenter' | 'vcenter';

/**
 * 整列。ユニットが 1 つならキャンバス基準、複数ならレイヤー順で最も前面にあるユニットを基準にする
 * （基準は動かさない）。
 */
export function alignUnits(
  slide: AdImageSlide,
  units: SelectionUnit[],
  mode: AlignMode,
  canvas: { width: number; height: number },
): AdElement[] {
  if (units.length === 0) return slide.elements;
  let ref: UnitBox;
  let refKey: string | null = null;
  if (units.length === 1) {
    ref = { x: 0, y: 0, w: canvas.width, h: canvas.height };
  } else {
    const order = slide.layerOrder?.length ? slide.layerOrder : slide.elements.map((e) => e.id);
    const frontId = order.find((id) => units.some((u) => u.memberIds.includes(id)));
    const refUnit = units.find((u) => frontId && u.memberIds.includes(frontId)) ?? units[0];
    refKey = refUnit.key;
    ref = unitBox(slide.elements, refUnit);
  }
  const deltas = new Map<string, { dx: number; dy: number }>();
  for (const u of units) {
    if (u.key === refKey) continue;
    const b = unitBox(slide.elements, u);
    let dx = 0;
    let dy = 0;
    switch (mode) {
      case 'left':
        dx = ref.x - b.x;
        break;
      case 'right':
        dx = ref.x + ref.w - (b.x + b.w);
        break;
      case 'hcenter':
        dx = Math.round(ref.x + ref.w / 2 - b.w / 2) - b.x;
        break;
      case 'top':
        dy = ref.y - b.y;
        break;
      case 'bottom':
        dy = ref.y + ref.h - (b.y + b.h);
        break;
      case 'vcenter':
        dy = Math.round(ref.y + ref.h / 2 - b.h / 2) - b.y;
        break;
    }
    deltas.set(u.key, { dx, dy });
  }
  return applyUnitDeltas(slide.elements, units, deltas);
}

/** 等間隔（中心点を等間隔に）。両端のユニットは動かさない。3 ユニット未満は何もしない。 */
export function distributeUnits(
  elements: AdElement[],
  units: SelectionUnit[],
  axis: 'horizontal' | 'vertical',
): AdElement[] {
  if (units.length < 3) return elements;
  const withBox = units.map((u) => ({ u, b: unitBox(elements, u) }));
  const center = (b: UnitBox) => (axis === 'horizontal' ? b.x + b.w / 2 : b.y + b.h / 2);
  withBox.sort((a, b) => center(a.b) - center(b.b));
  const firstC = center(withBox[0].b);
  const lastC = center(withBox[withBox.length - 1].b);
  const step = (lastC - firstC) / (withBox.length - 1);
  const deltas = new Map<string, { dx: number; dy: number }>();
  withBox.forEach(({ u, b }, idx) => {
    if (idx === 0 || idx === withBox.length - 1) return;
    const d = Math.round(firstC + step * idx - center(b));
    deltas.set(u.key, axis === 'horizontal' ? { dx: d, dy: 0 } : { dx: 0, dy: d });
  });
  return applyUnitDeltas(elements, units, deltas);
}

/** コピーした内容（ペースト先スライドが違っても使えるよう、要素とグループ定義を丸ごと持つ）。 */
export interface ElementClipboard {
  elements: AdElement[];
  /** 丸ごとコピーしたグループの定義（名前の引き継ぎ用） */
  groups: AdGroup[];
  /** 丸ごとコピーしたグループ ID */
  wholeGroupIds: string[];
}

/** 選択をユニット単位に広げて、複製元（要素 + 丸ごとのグループ）を取り出す。 */
export function collectForCopy(
  slide: AdImageSlide,
  selectedIds: string[],
  activeGroupId: string | null,
): ElementClipboard | null {
  const units = resolveUnits(slide.elements, selectedIds, activeGroupId);
  if (units.length === 0) return null;
  const ids = new Set(units.flatMap((u) => u.memberIds));
  const wholeGroupIds = units.map((u) => u.groupId).filter((g): g is string => !!g);
  return {
    elements: slide.elements.filter((e) => ids.has(e.id)).map((e) => structuredClone(e)),
    groups: (slide.groups ?? []).filter((g) => wholeGroupIds.includes(g.id)).map((g) => ({ ...g })),
    wholeGroupIds,
  };
}

function newId(prefix: string): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * 複製元をスライドに追加した結果を返す。
 * - 丸ごとコピーしたグループ → 新しいグループ ID を振り、「<元の名前> のコピー」として追加
 * - グループ編集中に一部だけコピーした要素 → ペースト先に同じグループがあればそこへ、無ければグループなし
 * - 新しい要素はレイヤーの最前面へ、元の前後関係を保って置く
 */
export function pasteClipboard(
  slide: AdImageSlide,
  clip: ElementClipboard,
  offsetXY: number,
): { slide: AdImageSlide; newIds: string[]; idMap: Map<string, string> } {
  const groupMap = new Map<string, string>();
  const existingGroups = slide.groups ?? [];
  const addedGroups: AdGroup[] = [];
  for (const gid of clip.wholeGroupIds) {
    const nid = newId('g');
    groupMap.set(gid, nid);
    const src = clip.groups.find((g) => g.id === gid);
    addedGroups.push({
      id: nid,
      name: src
        ? `${src.name} のコピー`
        : `グループ ${existingGroups.length + addedGroups.length + 1}`,
    });
  }
  const existingGroupIds = new Set(existingGroups.map((g) => g.id));
  const idMap = new Map<string, string>();
  const cloned = clip.elements.map((el) => {
    const id = newId('el');
    idMap.set(el.id, id);
    let groupId: string | undefined;
    if (el.groupId && groupMap.has(el.groupId)) groupId = groupMap.get(el.groupId);
    else if (el.groupId && existingGroupIds.has(el.groupId)) groupId = el.groupId;
    return {
      ...structuredClone(el),
      id,
      x: el.x + offsetXY,
      y: el.y + offsetXY,
      groupId,
    } as AdElement;
  });
  const currentOrder = slide.layerOrder?.length
    ? slide.layerOrder
    : slide.elements.map((e) => e.id);
  // 元のレイヤー順（コピー元スライドの順）を保つ。元の順が分からない要素は末尾
  const srcOrder = clip.elements.map((e) => e.id);
  const orderedNew = [...srcOrder]
    .sort((a, b) => {
      const ia = currentOrder.indexOf(a);
      const ib = currentOrder.indexOf(b);
      return (ia < 0 ? Number.MAX_SAFE_INTEGER : ia) - (ib < 0 ? Number.MAX_SAFE_INTEGER : ib);
    })
    .map((oid) => idMap.get(oid)!);
  return {
    slide: {
      ...slide,
      elements: [...slide.elements, ...cloned],
      layerOrder: [...orderedNew, ...currentOrder],
      groups: [...existingGroups, ...addedGroups],
    },
    newIds: cloned.map((c) => c.id),
    idMap,
  };
}
